import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import API_ENDPOINTS from "../../apis/endpoints";
import {
    Button,
    Typography,
    useTheme,
    Snackbar,
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import useAuth from "../../context";
import Editor from "uwp-email-builder";
import { useNavigate, useSearchParams } from "react-router-dom";

function NewsletterEditor() {
    const emailEditorRef = useRef(null);
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = useAuth();
    const [openDialog, setOpenDialog] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const storedDeviceId = localStorage.getItem("device-id");
    // Add these state variables
    const [openDraftDialog, setOpenDraftDialog] = useState(false);
    const [openContinueDialog, setOpenContinueDialog] = useState(false);
    const [newsletterName, setNewsletterName] = useState("");
    const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [templateSnapshot, setTemplateSnapshot] = useState(null);

    const [template, setTemplate] = useState({
        html: "",
        design: null,
    });

    const {
        data,
        isLoading: dataLoading,
        mutate,
    } = useSWR(
        () =>
            searchParams.has("template")
                ? `${API_ENDPOINTS.NEWSLETTER}template/${searchParams.get(
                      "template"
                  )}/`
                : searchParams.has("edit")
                  ? `${API_ENDPOINTS.NEWSLETTER}${searchParams.get(
                        "edit"
                    )}/template/`
                  : null,
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            revalidateOnReconnect: false,
        }
    );

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleFileUpload = function (file, done) {
        let filename = file.name;

        if (!filename.includes(".")) {
            filename += ".png";
        }

        fetch(`${API_ENDPOINTS.STORAGE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.accessToken}`,
                "X-CC-Device-ID": auth.deviceId,
            },
            body: JSON.stringify({
                public: true,
                folder_type: "newsletter",
                folder_id: "common",
                file_name: filename,
                file_type: file.type,
                file_size: file.size,
            }),
        })
            .then((response) => {
                done({ progress: 10 });
                if (response.status === 200) {
                    return response;
                } else {
                    setSnackbar(response.statusText);
                    setSnackbarOpen(true);
                    return done({ error: response.statusText });
                }
            })
            .then((response) => {
                done({ progress: 20 });
                return response.json();
            })
            .then((data) => {
                done({ progress: 30 });
                if (!data || data.error || !data.data) {
                    const message = data?.message || "Invalid response";
                    setSnackbar(message);
                    setSnackbarOpen(true);
                    done({ error: true });
                    return done({ error: message });
                }

                return {
                    upload_url: data.data.upload_url,
                    file_url: data.data.url,
                };
            })
            .then((data) => {
                done({ progress: 40 });

                let progress = 40;
                const interval = setInterval(() => {
                    progress += 1;
                    done({ progress });
                    if (progress >= 80) {
                        clearInterval(interval);
                    }
                }, 300);

                fetch(data.upload_url, {
                    method: "PUT",
                    headers: {
                        "Content-Type": file.type,
                    },
                    body: file,
                }).then((response) => {
                    clearInterval(interval);
                    done({ progress: 90 });
                    if (!response.ok) {
                        setSnackbar(response.statusText);
                        setSnackbarOpen(true);
                        done({ error: true });
                        return done({ error: response.statusText });
                    }

                    done({ url: data.file_url });
                });
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleTemplateUpdate = (data) => {
        setTemplate(data);
    };

    const templateData = useMemo(() => {
        if (!data) {
            return null;
        }

        return data.design;
    }, [data]);

    if (dataLoading) {
        return (
            <Box className="loader-container">
                <CircularProgress />
            </Box>
        );
    }

    const handleCloseTemplateDialog = () => {
        setOpenTemplateDialog(false);
        setTemplateName("");
    };

    const handleCloseDraftDialog = () => {
        setOpenDraftDialog(false);
        setNewsletterName("");
    };

    const handleSaveTemplate = async () => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(template.design));
            formData.append("name", templateName);

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}template/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save template");
            }
            setSnackbar({
                open: true,
                message: "Newsletter saved as Template successfully",
                severity: "success",
            });

            mutate();
            handleCloseTemplateDialog();
            // navigate("/dashboard/newsletter/list");
        } catch (error) {
            console.error("Error saving template:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
        handleCloseTemplateDialog();
    };

    const handleSaveDraft = async () => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(template.design));
            formData.append("html", template.html);
            formData.append("name", newsletterName);

            const response = await fetch(`${API_ENDPOINTS.NEWSLETTER}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Failed to save draft");
            }
            setSnackbar({
                open: true,
                message: "Newsletter saved as draft successfully",
                severity: "success",
            });

            const result = await response.json();

            const { id } = result.data;

            handleCloseDraftDialog();

            if (!searchParams.has("edit")) {
                navigate(`/dashboard/newsletter/create?edit=${id}`, {
                    replace: true,
                });
            }

            mutate();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message || "Failed to save newsletter",
                severity: "error",
            });
            console.error("Error saving draft:", error);
        }
    };

    const handleUpdateNewsletter = async (id, data, redirect) => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(data.design));
            formData.append("html", data.html);

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}${id}/template/`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save newsletter");
            }
            setSnackbar({
                open: true,
                message: "Newsletter saved as draft successfully",
                severity: "success",
            });
            const result = await response.json();

            mutate();
            if (redirect) {
                navigate(`/dashboard/newsletter/send/${id}`);
            }
        } catch (error) {
            console.error("Error saving newsletter:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
    };

    const handleOpenTemplateDialog = () => {
        setOpenTemplateDialog(true);
    };

    const handleContinue = async (data) => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(data.design));
            formData.append("html", data.html);
            formData.append("name", newsletterName || "Untitled");

            const response = await fetch(`${API_ENDPOINTS.NEWSLETTER}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Failed to save newsletter");
            }

            const result = await response.json();

            mutate();
            navigate(`/dashboard/newsletter/create?edit=${result.data.id}`, {
                replace: true,
            });

            setTimeout(() => {
                navigate(`/dashboard/newsletter/send/${result.data.id}`);
            }, 0);
        } catch (error) {
            console.error("Error saving newsletter:", error);
        }
    };

    const handleOpenDraftDialog = () => {
        if (searchParams.get("edit") && data) {
            handleUpdateNewsletter(searchParams.get("edit"), template);
        } else {
            setOpenDraftDialog(true);
        }
    };

    const handleOpenContinueDialog = () => {
        if (searchParams.get("edit") && data) {
            handleUpdateNewsletter(searchParams.get("edit"), template, true);
        } else {
            handleContinue(template);
        }
    };

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        columnGap: "1rem",
                        alignItems: "center",
                    }}
                >
                    <ArrowBackIosNewRoundedIcon
                        onClick={() => navigate("/dashboard/newsletter")}
                    />
                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[200] }}
                    >
                        1/2
                    </Typography>
                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        Design Newsletter
                    </Typography>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        columnGap: "1rem",
                        alignItems: "center",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleOpenTemplateDialog}
                        disabled={dataLoading}
                    >
                        Add to Template
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleOpenDraftDialog}
                        disabled={dataLoading}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleOpenContinueDialog}
                        disabled={dataLoading}
                    >
                        Continue
                    </Button>
                </div>
            </div>
            <div
                style={{
                    height: "87vh",
                    // display: "flex",
                    // width: "100vw !important",
                }}
            >
                <Editor
                    article={`${API_ENDPOINTS.NEWSLETTER}articles/`}
                    category={`${API_ENDPOINTS.NEWSLETTER}category/`}
                    onSnapshot={setTemplateSnapshot}
                    template={templateData}
                    onFileUpload={handleFileUpload}
                    onChange={handleTemplateUpdate}
                />
            </div>

            <Dialog
                open={openTemplateDialog}
                onClose={handleCloseTemplateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save as Template</DialogTitle>
                <DialogContentText sx={{ marginLeft: "1.5rem" }}>
                    You can Add this NewsLetter as template to use it later and
                    use it whenever required
                </DialogContentText>
                <DialogContent sx={{ width: "100%", pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Template Name"
                        fullWidth
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
                    <Button onClick={handleSaveTemplate} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Draft Dialog */}
            <Dialog
                open={openDraftDialog}
                onClose={handleCloseDraftDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save NewsLetter</DialogTitle>
                <DialogContentText sx={{ marginLeft: "1.5rem" }}>
                    You can save NewsLetter as draft and can continue editing
                    whenever required
                </DialogContentText>
                <DialogContent sx={{ width: "100%", pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Newsletter Name"
                        fullWidth
                        value={newsletterName}
                        onChange={(e) => setNewsletterName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDraftDialog}>Cancel</Button>
                    <Button onClick={handleSaveDraft} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default NewsletterEditor;
